<!--
  CollectorLineCard — the line card in its shapes (collector-app P1-D, handoff
  spec Section 2.5; mockup Line.dc.html).

    group   shape A: lines sharing a `groupKey` folded into ONE card — the
            product, the post's photos once, the product's three figures, then
            one row per member: her quantity in a square, the chips, her
            amount, her own ✓ / ✗ (a mark marks ONE row). A row that carries
            instructions OPENS: her photo beside her comment, the leader's
            note, then the row's status line. «اتجاب الباقي كله» marks the
            PLAIN rows only (owner decision 5) — a row with instructions has to
            be read, so it is marked on its own. Then the door to «بلّغ من
            الرف».
    single  shape B: one line — her photo first (120 px, the purple ring),
            the chips, her comment and the leader's note in their own blocks
            (CollectorLineNotes), quantity × price = amount as three figures
            (CollectorFigures), the earlier-tries strip, the two big buttons,
            the door.
            Shape C is this card waiting on a member question: the compact
            product row, her comment and the leader's note, the lilac «الليدر
            بعت سؤال…» block, NO mark buttons (the status machine refuses a
            mark while a question is live). Once she answers, the green block
            takes its place — her words and the server's instruction, with
            «اشتريته» / «مش لقيته» as REPORTS to the leader (CollectorLineAnswer;
            collector-app P3-C), still no mark.
            Once MARKED (or verified by the leader) the card shrinks to one
            row: a small photo, the name and the chips, what the mark really
            came to (CollectorLineResult) and «ارجّع». A line that carries
            instructions keeps them: her photo (64 px, the ring) and both
            blocks at 13 px. No height animation.
    dashed  a line the leader released («لاغي») or moved to another collector.

  RULE G (collector-clarity, handoff spec Section 3): her photo, her comment
  and the leader's notes each have their own full-width place on every shape.
  Nothing else on the line ever replaces one, and neither text is ever clamped.

  Presentational: it reads lines (the day document) and emits what was tapped;
  the shop page turns the taps into actions through the action layer. What a
  row can do is the backend's own flag (`canMark`, `canReopen`, …) via
  collector/lineView.js — nothing is recomputed here.
-->
<template>
  <article
    class="c-card"
    :class="cardClasses"
    :data-kind="card.kind"
    :data-outcome="card.lines[0]?.outcome"
  >
    <!-- ── dashed: released / moved ───────────────────────────────────────── -->
    <template v-if="card.kind === 'dashed'">
      <div class="c-card__title">
        {{ first.productName }}
      </div>
      <collector-line-chips :line="first" size="sm"></collector-line-chips>
      <div v-if="first.movedAway" class="c-card__note">
        {{ t('collector.line.moved') }}
      </div>
      <collector-line-status v-else :line="first"></collector-line-status>
    </template>

    <!-- ── shape A: grouped ───────────────────────────────────────────────── -->
    <template v-else-if="card.kind === 'group'">
      <button
        type="button"
        class="c-reset c-card__open"
        :aria-label="t('collector.card.openLine')"
        @click="emit('open', first)"
      >
        <span class="c-head">
          <span class="c-card__title c-card__title--lg">{{ first.productName }}</span>
          <span class="c-card__meta c-num">{{ groupMeta }}</span>
        </span>

        <span v-if="strip.length" class="c-strip">
          <collector-photo
            size="hero"
            :src="photoThumb(strip[0])"
            :tag="t(tierTag(strip[0]))"
          ></collector-photo>
          <collector-photo
            v-for="(photo, index) in strip.slice(1, 3)"
            :key="`${photo.url}-${index}`"
            size="md"
            :src="photoThumb(photo)"
          ></collector-photo>
          <collector-photo
            v-if="stripHidden > 0"
            size="md"
            :more="stripHidden"
          ></collector-photo>
        </span>

        <collector-figures :lines="card.lines"></collector-figures>
      </button>

      <div class="c-members">
        <div
          v-for="line in card.lines"
          :key="line.id"
          class="c-member"
          :data-state="lineState(line)"
        >
          <div class="c-member__top">
            <button
              type="button"
              class="c-reset c-member__body"
              :aria-label="t('collector.card.openLine')"
              @click="emit('open', line)"
            >
              <span class="c-qty c-num" :class="qtyTone(line)">
                <b class="c-qty__n">{{ line.qty }}</b>
                <small class="c-qty__word">{{ countWord('pieces', line.qty, t) }}</small>
              </span>
              <span class="c-member__text">
                <collector-line-chips :line="line" size="sm" row></collector-line-chips>
                <collector-line-result v-if="isMarked(line)" :line="line" row></collector-line-result>
                <span v-else class="c-member__amount c-num">{{ rowAmount(line) }}</span>
              </span>
            </button>

            <div v-if="lineState(line) === 'markable' || rowReports(line)" class="c-member__act">
              <button
                type="button"
                class="c-reset c-mini c-mini--ok"
                :aria-label="rowReports(line) ? t('collector.answer.bought') : t('collector.card.markAria')"
                @click="rowOk(line)"
              >
                <svg width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                ><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
              </button>
              <button
                type="button"
                class="c-reset c-mini c-mini--no"
                :aria-label="rowReports(line) ? t('collector.answer.not_found') : t('collector.card.oosAria')"
                @click="rowNo(line)"
              >
                <svg width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                ><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <collector-line-status
              v-else
              compact
              :line="line"
              @reopen="(target) => emit('reopen', target)"
            ></collector-line-status>
          </div>

          <!-- Rule G: what she and the leader said opens under the row's main
               line, whole. Whatever else the row says STACKS under it. Both
               open the line, as the row's old one-line fact did. No aria-label
               here: the button is named by what it says, so a screen reader
               reads her comment and the leader's note instead of a bare
               «تفاصيل الصنف». -->
          <button
            v-if="hasInstructions(line) || memberFact(line).text"
            type="button"
            class="c-reset c-member__notes"
            @click="emit('open', line)"
          >
            <collector-line-notes
              v-if="hasInstructions(line)"
              :line="line"
              photo="beside"
            ></collector-line-notes>
            <span
              v-if="memberFact(line).text"
              class="c-member__fact"
              :class="`c-member__fact--${memberFact(line).kind}`"
            >{{ memberFact(line).text }}</span>
          </button>
        </div>
      </div>

      <div v-if="rest.length > 1 || reportTarget" class="c-card__foot">
        <button
          v-if="rest.length > 1"
          type="button"
          class="c-reset c-foot-btn c-foot-btn--ok c-num"
          @click="emit('mark-rest', rest)"
        >
          {{ t('collector.card.markRestCount', { pieces: restPieces }) }}
        </button>
        <button
          v-if="reportTarget"
          type="button"
          class="c-reset c-foot-btn c-foot-btn--door"
          @click="emit('report', reportTarget)"
        >
          {{ t('collector.card.door') }}
        </button>
      </div>
    </template>

    <!-- ── shape C: waiting on a member question ──────────────────────────── -->
    <template v-else-if="waiting">
      <button
        type="button"
        class="c-reset c-card__open"
        :aria-label="t('collector.card.openLine')"
        @click="emit('open', first)"
      >
        <span class="c-compact">
          <collector-photo
            :size="leadIsHers ? 'lg' : 'rail'"
            :framed="leadIsHers"
            :src="photoThumb(photos[0])"
            :tag="photos[0] ? t(tierTag(photos[0], leadIsHers ? 'long' : 'short')) : null"
          ></collector-photo>
          <span class="c-compact__text">
            <span class="c-card__title">{{ first.productName }}</span>
            <collector-line-chips :line="first" size="sm"></collector-line-chips>
            <span class="c-card__meta c-num">{{ qtySentence }}</span>
          </span>
        </span>
        <collector-line-notes :line="first"></collector-line-notes>
      </button>
      <div v-if="reported && !answer?.reported" class="c-block c-block--lilac">
        {{ reportedText }}
      </div>
      <collector-line-answer
        v-if="answer?.answered"
        :line="first"
        @report="(line, type) => emit('report-answer', line, type)"
      ></collector-line-answer>
      <div v-else class="c-block c-block--lilac c-block--soft">
        {{ waitingText }}
      </div>
    </template>

    <!-- ── a marked line: what he really paid, and out of the way ─────────── -->
    <template v-else-if="settled">
      <div class="c-marked">
        <button
          type="button"
          class="c-reset c-card__open c-marked__open"
          :aria-label="t('collector.card.openLine')"
          @click="emit('open', first)"
        >
          <collector-photo
            class="c-marked__photo"
            :size="leadIsHers ? 'md' : 'sm'"
            :framed="leadIsHers"
            :src="photoThumb(photos[0])"
            :tag="leadIsHers ? t(tierTag(photos[0], 'short')) : null"
          ></collector-photo>
          <span class="c-marked__text">
            <span class="c-marked__head">
              <span class="c-card__title c-card__title--sm">{{ first.productName }}</span>
              <collector-line-chips :line="first" size="sm"></collector-line-chips>
            </span>
            <collector-line-result :line="first"></collector-line-result>
          </span>
        </button>
        <collector-line-status
          compact
          :line="first"
          @reopen="(target) => emit('reopen', target)"
        ></collector-line-status>
      </div>
      <!-- A marked line keeps what she and the leader said, so a wrong mark can
           still be caught against what was asked — and a reservation, so a
           deposit sitting at the shop under her name is not forgotten. -->
      <button
        v-if="settledNotes || first.reservedForName"
        type="button"
        class="c-reset c-marked__notes"
        @click="emit('open', first)"
      >
        <collector-line-notes v-if="settledNotes" :line="first" size="sm"></collector-line-notes>
        <span v-if="first.reservedForName" class="c-member__fact c-member__fact--reserved c-num">
          {{ t('collector.card.reserved', { name: first.reservedForName, deposit: pounds(first.depositCents) }) }}
        </span>
      </button>
    </template>

    <!-- ── shape B: one line ──────────────────────────────────────────────── -->
    <template v-else>
      <div v-if="rejected" class="c-banner">
        {{ rejectedText }}
      </div>

      <button
        type="button"
        class="c-reset c-card__open"
        :aria-label="t('collector.card.openLine')"
        @click="emit('open', first)"
      >
        <span class="c-single">
          <collector-photo
            :size="leadIsHers ? 'xl' : 'lg'"
            :framed="leadIsHers"
            :src="photoThumb(photos[0])"
            :tag="photos[0] ? t(tierTag(photos[0])) : null"
          ></collector-photo>
          <span class="c-single__text">
            <span class="c-card__title c-card__title--lg">{{ first.productName }}</span>
            <collector-line-chips :line="first"></collector-line-chips>
            <span v-if="photos.length > 1" class="c-single__thumbs">
              <collector-photo
                v-for="(photo, index) in photos.slice(1, 3)"
                :key="`${photo.url}-${index}`"
                size="sm"
                :src="photoThumb(photo)"
                :tag="t(tierTag(photo, 'short'))"
              ></collector-photo>
              <collector-photo
                v-if="photos.length > 3"
                size="sm"
                :more="photos.length - 3"
              ></collector-photo>
            </span>
          </span>
        </span>
        <collector-line-notes :line="first"></collector-line-notes>
        <collector-figures :line="first" :foot="!first.reservedForName"></collector-figures>
        <span v-if="first.reservedForName" class="c-block c-block--amber c-num">
          {{ t('collector.card.reserved', { name: first.reservedForName, deposit: pounds(first.depositCents) }) }}
        </span>
      </button>

      <div v-if="reported" class="c-block c-block--lilac">
        {{ reportedText }}
      </div>

      <div v-if="tries.length" class="c-tries">
        <div class="c-tries__head">
          {{ triesHead(tries[0]) }}
        </div>
        <div v-if="triesNote(tries[0])" class="c-tries__note">
          {{ triesNote(tries[0]) }}
        </div>
      </div>

      <div v-if="state === 'markable'" class="c-actions">
        <button type="button" class="c-reset c-big c-big--ok" @click="emit('mark', first, 'collected')">
          {{ t('collector.line.collected') }}
        </button>
        <button type="button" class="c-reset c-big c-big--no" @click="emit('mark', first, 'out_of_stock')">
          {{ t('collector.line.oos') }}
        </button>
      </div>

      <button
        v-if="reportTarget"
        type="button"
        class="c-reset c-door"
        @click="emit('report', reportTarget)"
      >
        {{ t('collector.card.doorFull') }}
      </button>
    </template>
  </article>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { countBucket, countPhrase, countWord, formatClock, phraseText } from '@/collector/dayView.js';
import {
  answerSaidPhrase,
  answerView,
  groupTotals,
  hasInstructions,
  hiddenPhotoCount,
  isRejected,
  lineInstructions,
  lineState,
  memberAnswerKey,
  orderedPhotos,
  photoThumb,
  reportSummary,
  rowStatusFact,
  stripPhotos,
  sweepableLines,
  tierTag,
  tryDay,
  tryOutcomeKey,
} from '@/collector/lineView.js';
import { groupMoney, linePayableCents } from '@/collector/moneyView.js';
import CollectorFigures from '@/components/collector/CollectorFigures.vue';
import CollectorLineAnswer from '@/components/collector/CollectorLineAnswer.vue';
import CollectorLineChips from '@/components/collector/CollectorLineChips.vue';
import CollectorLineNotes from '@/components/collector/CollectorLineNotes.vue';
import CollectorLineResult from '@/components/collector/CollectorLineResult.vue';
import CollectorLineStatus from '@/components/collector/CollectorLineStatus.vue';
import CollectorPhoto from '@/components/collector/CollectorPhoto.vue';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  /** `{ kind: 'group'|'single'|'dashed', key, lines }` from collector/lineView.js `foldLines`. */
  card: { type: Object, required: true },
});
const emit = defineEmits(['mark', 'mark-rest', 'reopen', 'report', 'report-answer', 'open']);

const { t } = useI18n();
const { formatAmount } = useCurrency();
// Nothing is floored, so an amount can go below zero: the sign is U+2212 and
// stays on its digits (Intl keeps it inside its LRM).
const pounds = (cents) => formatAmount(cents, { trimZeros: true }).replace('-', '−');
const pieces = (qty) => phraseText(countPhrase('pieces', qty ?? 0), t, pounds);

const first = computed(() => props.card.lines[0]);
const state = computed(() => lineState(first.value));
const rejected = computed(() => isRejected(first.value));
const waiting = computed(() => state.value === 'question');
/** My mark stands (or the leader verified the line): the single card shrinks to one row. */
const settled = computed(() => state.value === 'marked' || state.value === 'verified');
const reported = computed(() => reportSummary(first.value));
/** Her answer to the live question (shape C), or the question still waiting. */
const answer = computed(() => answerView(first.value));

const cardClasses = computed(() => ({
  'c-card--group': props.card.kind === 'group',
  'c-card--dashed': props.card.kind === 'dashed',
  'c-card--waiting': props.card.kind === 'single' && waiting.value,
  'c-card--rejected': props.card.kind === 'single' && rejected.value,
  'c-card--verified': props.card.kind === 'single' && state.value === 'verified',
  'c-card--marked': props.card.kind === 'single' && settled.value,
  'c-card--oos': props.card.kind === 'single' && settled.value && first.value.outcome === 'out_of_stock',
}));

// ── Photos ────────────────────────────────────────────────────────────────────

/**
 * The line's own photo row: hers first, then the post's and the rest. The
 * leader's instruction photos are NOT here — they have one home, beside the
 * leader's note (CollectorLineNotes), so none is drawn twice.
 */
const photos = computed(() => orderedPhotos(first.value).filter((photo) => photo.tier !== 'brief'));
/** HER photo leads the line (`orderedPhotos` puts it first): the largest tile, with the ring. */
const leadIsHers = computed(() => photos.value[0]?.tier === 'member');
/**
 * What the marked card's 13 px blocks can draw: her comment, the leader's note
 * or the leader's photos. Her own photo is in the row itself, so a line whose
 * only instruction is her photo has no block to open.
 */
const settledNotes = computed(() => {
  const { note, brief, briefPhotos } = lineInstructions(first.value);
  return note != null || brief != null || briefPhotos.length > 0;
});
/**
 * The group's shared strip: the post's photos. Hers and the leader's are about
 * ONE member's line, so they are drawn on her row and never here.
 */
const strip = computed(() => stripPhotos(props.card.lines));
/** The hero + two thumbs fit a 390 px card with the «+N» face; more hides behind it. */
const stripHidden = computed(() => hiddenPhotoCount(strip.value.length, 3));

// ── Shape A ───────────────────────────────────────────────────────────────────

/** «قناة «ريهام كولكشن» · 3 عضوات» — the pieces and the price are the figures' to say. */
const groupMeta = computed(() => {
  const { members: count } = groupTotals(props.card.lines);
  const members = t(`collector.count.members.${countBucket(count)}`, { n: count });
  const channel = first.value.channelName;
  return channel ? t('collector.card.groupMeta', { channel, members }) : members;
});

/** Lines of one product can carry different prices: each row then prints its own. */
const mixedPrices = computed(() => groupMoney(props.card.lines).unitCents === null);

/** A row whose mark stands: its second line is what the mark came to (CollectorLineResult). */
function isMarked(line) {
  const rowState = lineState(line);
  return rowState === 'marked' || rowState === 'verified';
}

/** The quantity square: green once collected, greyed when there is nothing to buy on the row. */
function qtyTone(line) {
  const rowState = lineState(line);
  if (rowState === 'released' || rowState === 'moved' || line.outcome === 'out_of_stock') return 'c-qty--off';
  return line.outcome === 'collected' ? 'c-qty--ok' : null;
}

/**
 * Her amount while the row is open: «960 ج.م», or «3 × 320 = 960 ج.م» when the
 * group's prices are mixed. With her deposit the row says what he PAYS in
 * words after it — «· تدفع 760» — so the sum always holds and the smaller
 * figure is never read as the line's total.
 */
function rowAmount(line) {
  const unit = Number(line.unitPriceCents) || 0;
  if (unit <= 0) return '—';
  const total = pounds(line.totalCents);
  const amount = mixedPrices.value
    ? t('collector.result.sum', { qty: line.qty, price: pounds(unit), total })
    : t('collector.line.price', { price: total });
  return Number(line.depositCents) > 0
    ? `${amount} · ${t('collector.figures.pay')} ${pounds(linePayableCents(line))}`
    : amount;
}

/**
 * The rows «اتجاب الباقي كله» marks: the PLAIN ones still taking a mark (owner
 * decision 5). The shop page marks exactly what it is handed.
 */
const rest = computed(() => sweepableLines(props.card.lines));
/** «5 قطع» — the pieces the button stands for. */
const restPieces = computed(() => pieces(rest.value.reduce((sum, line) => sum + (Number(line.qty) || 0), 0)));

/** The row «المتاح بدل كده» reports on: the first line still without a mark. */
const reportTarget = computed(
  () => props.card.lines.find((line) => line.outcome === 'pending' && !line.leaderState?.verified) ?? null,
);

/**
 * The status line under a member's row, with its text resolved. Her comment is
 * NOT one of these any more — it has a block of its own, and these stack with it.
 */
function memberFact(line) {
  const fact = rowStatusFact(line);
  switch (fact.kind) {
    case 'rejected':
      return { kind: 'rejected', text: rejectedTextOf(line) };
    case 'question':
      return { kind: 'question', text: questionTextOf(line) };
    case 'answered':
      return answeredFact(line);
    case 'reserved':
      return {
        kind: 'reserved',
        text: t('collector.card.reserved', { name: line.reservedForName, deposit: pounds(line.depositCents) }),
      };
    case 'reported':
      return { kind: 'reported', text: reportedTextOf(line) };
    case 'agreed':
      return { kind: 'agreed', text: t('collector.card.agreed', { text: fact.text }) };
    default:
      return { kind: 'none', text: null };
  }
}

/**
 * Her answer on a member row: green when it is a yes (a thing to buy), amber
 * when it says leave it, indigo once I have reported on it.
 */
function answeredFact(line) {
  const view = answerView(line);
  if (view.reported) return { kind: 'answer-done', text: t(`collector.answer.reported.${view.reported}`) };
  const phrase = answerSaidPhrase(view);
  return { kind: view.canReport ? 'answered' : 'answered-stop', text: t(phrase.key, phrase.params) };
}

/** A row whose yes still waits for «اشتريته» / «مش لقيته»: its two small buttons report instead of mark. */
function rowReports(line) {
  if (lineState(line) !== 'question') return false;
  const view = answerView(line);
  return view?.answered === true && view.canReport && view.reported == null;
}

function rowOk(line) {
  if (rowReports(line)) emit('report-answer', line, 'bought');
  else emit('mark', line, 'collected');
}

function rowNo(line) {
  if (rowReports(line)) emit('report-answer', line, 'not_found');
  else emit('mark', line, 'out_of_stock');
}

// ── Shared words ──────────────────────────────────────────────────────────────

/** «قطعتين × 175 = 350 ج.م» — the waiting card's one line of figures (her code is a chip). */
const qtySentence = computed(() => {
  const priced = Number(first.value.unitPriceCents) > 0;
  return t('collector.line.qtyPrice', {
    qty: pieces(first.value.qty),
    price: priced ? pounds(first.value.unitPriceCents) : '—',
    total: priced ? pounds(first.value.totalCents) : '—',
  });
});

function rejectedTextOf(line) {
  const reason = line.leaderState.rejectedReason;
  return reason ? t('collector.line.rejected', { reason }) : t('collector.line.rejectedNoReason');
}

const rejectedText = computed(() => rejectedTextOf(first.value));

function questionTextOf(line) {
  const time = formatClock(line.activeQuestion?.sentAt);
  return time ? t('collector.line.question', { time }) : t('collector.line.questionNoTime');
}

const waitingText = computed(() => questionTextOf(first.value));

/**
 * «بلّغت 11:05: متاح: أسود، بيج · «…» + صورة الرف»: the colours he picked, then
 * his words; the bare facts when he wrote and picked nothing.
 */
function reportedTextOf(line) {
  const report = reportSummary(line);
  if (!report) return '';
  const time = formatClock(report.at) ?? '';
  const said = [];
  if (report.options.length) said.push(t('collector.card.reportedOptions', { options: report.options.join('، ') }));
  if (report.note) said.push(`«${report.note}»`);
  let text;
  if (said.length) {
    text = t('collector.card.reported', { time, what: said.join(' · ') });
  } else {
    const facts = [];
    if (report.foundQty != null) facts.push(t('collector.card.reportedFound', { found: report.foundQty, qty: line.qty }));
    if (report.priceCents != null) facts.push(t('collector.card.reportedPrice', { price: pounds(report.priceCents) }));
    text = [t('collector.card.reportedBare', { time }), ...facts].join(' · ');
  }
  return report.hasPhotos ? `${text}${t('collector.card.reportedPhoto')}` : text;
}

const reportedText = computed(() => reportedTextOf(first.value));

// ── Earlier tries ─────────────────────────────────────────────────────────────

const tries = computed(() => (Array.isArray(first.value.tries) ? first.value.tries : []));

/** «اتجرّب امبارح عند «ريهام جاليري»: مش موجود» */
function triesHead(attempt) {
  const day = tryDay(attempt.date);
  const dayText = day.key ? t(day.key) : day.date;
  const outcome = t(tryOutcomeKey(attempt.outcome));
  return attempt.shop
    ? t('collector.tries.strip', { day: dayText, shop: attempt.shop, outcome })
    : t('collector.tries.stripNoShop', { day: dayText, outcome });
}

/** «اللي جرّب كتب: «…» · العضوة رفضت البديل» */
function triesNote(attempt) {
  const parts = [];
  if (attempt.collectorNote) parts.push(t('collector.tries.note', { note: attempt.collectorNote }));
  const answerKey = memberAnswerKey(attempt.memberAnswer);
  if (answerKey) parts.push(t(answerKey));
  return parts.join(' · ');
}
</script>

<style scoped>
.c-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  background: var(--c-card);
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-card);
}

.c-card--waiting { border: 2px solid var(--c-purple); }
.c-card--rejected { border: 2px solid var(--c-amber-border); }
.c-card--verified { opacity: 0.85; }

.c-card--dashed {
  gap: 8px;
  border-style: dashed;
  border-color: var(--c-dot);
  opacity: 0.6;
}

.c-card--dashed .c-card__title { text-decoration: line-through; }

/* A card-wide tap target that is not itself a button-shaped thing. */
.c-card__open {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  color: var(--c-ink);
  text-align: start;
}

.c-card__title {
  font-size: 16px;
  font-weight: 700;
}

.c-card__title--lg { font-size: 17px; }
.c-card__title--sm { font-size: 15px; }

.c-card__meta {
  font-size: 13px;
  color: var(--c-muted);
}

.c-card__note {
  font-size: 13px;
  color: var(--c-muted);
}

/* ── Shape A ── */
.c-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.c-strip {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}

.c-members {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.c-member {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px;
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
}

.c-member[data-state='released'],
.c-member[data-state='moved'] { opacity: 0.6; }

.c-member__top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.c-member__body {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 0;
  min-width: 0;
  text-align: start;
}

/* Her quantity, where the repeated post thumbnail used to be. */
.c-qty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 10px;
  background: var(--c-indigo);
  color: #ffffff;
  line-height: 1;
}

.c-qty__n {
  font-size: 21px;
  font-weight: 700;
}

.c-qty__word {
  margin-top: 2px;
  font-size: 10px;
  color: var(--c-head-sub);
}

.c-qty--ok { background: var(--c-green-text); }
.c-qty--ok .c-qty__word { color: var(--c-green-fill); }

/* white fails on --c-dot (1.7:1): the greyed square is read in ink */
.c-qty--off { background: var(--c-dot); color: var(--c-ink); }
.c-qty--off .c-qty__word { color: var(--c-ink); }

.c-member__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 0;
  min-width: 0;
}

.c-member__amount {
  font-size: 13px;
  font-weight: 700;
  color: var(--c-ink);
}

.c-member__notes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: start;
}

/* The row's status line, whole: it wraps, it is never cut. */
.c-member__fact {
  font-size: 12px;
  font-weight: 700;
  color: var(--c-muted);
  line-height: 1.4;
}

.c-member__fact--reserved,
.c-member__fact--rejected { color: var(--c-amber-pill); }
.c-member__fact--agreed { color: var(--c-green-text); }
.c-member__fact--reported,
.c-member__fact--question,
.c-member__fact--answer-done { color: var(--c-indigo); }
.c-member__fact--answered { color: var(--c-green-text); }
.c-member__fact--answered-stop { color: var(--c-amber-pill); }

.c-member__act {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* «ارجّع» is ground-filled on a card; the row IS the ground, so here it is a card */
.c-member :deep(.c-status__reopen) { background: var(--c-card); }

.c-mini {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--c-radius-chip);
  box-sizing: border-box;
}

.c-mini--ok { background: var(--c-green-text); color: #ffffff; }
.c-mini--no { border: 2px solid var(--c-red-border); background: var(--c-card); color: var(--c-red-text); }

.c-card__foot {
  display: flex;
  gap: 8px;
}

.c-foot-btn {
  flex: 1 1 0;
  min-height: 46px;
  padding: 4px 8px;
  border-radius: var(--c-radius-chip);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

/* «اتجاب الباقي كله · 5 قطع» is the longer of the two labels */
.c-foot-btn--ok { flex-grow: 1.4; background: var(--c-green-text); color: #ffffff; font-size: 15px; }
.c-foot-btn--door { border: 1px solid var(--c-border); background: var(--c-ground); color: var(--c-indigo); }

/* ── Shape B ── */
.c-banner {
  padding: 8px 12px;
  border-radius: var(--c-radius-chip);
  background: var(--c-amber-fill);
  color: var(--c-amber-text);
  font-size: 13px;
  font-weight: 700;
}

.c-single {
  display: flex;
  gap: 12px;
}

.c-single__text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 0;
  min-width: 0;
}

.c-single__thumbs {
  display: flex;
  gap: 6px;
}

.c-block {
  display: block;
  padding: 8px 12px;
  border-radius: var(--c-radius-chip);
  font-size: 13px;
  font-weight: 700;
}

.c-block--amber { background: var(--c-amber-fill); color: var(--c-amber-text); }
.c-block--lilac { background: var(--c-lilac); color: var(--c-indigo); }
.c-block--soft { color: var(--c-muted); }

.c-tries {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: var(--c-radius-chip);
  background: var(--c-amber-fill);
}

.c-tries__head { font-size: 13px; font-weight: 700; color: var(--c-amber-text); }
.c-tries__note { font-size: 12px; color: var(--c-amber-ink); }

.c-actions {
  display: flex;
  gap: 10px;
}

.c-big {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  height: 52px;
  border-radius: var(--c-radius-button);
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  box-sizing: border-box;
}

.c-big--ok { background: var(--c-green-text); color: #ffffff; }
.c-big--no { border: 2px solid var(--c-red-border); background: var(--c-card); color: var(--c-red-text); }

.c-door {
  height: 44px;
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-indigo);
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

/* ── A marked line ── */
.c-card--marked {
  gap: 8px;
  padding: 10px 12px;
}

.c-marked {
  display: flex;
  align-items: center;
  gap: 10px;
}

.c-marked__open {
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex: 1 1 0;
  min-width: 0;
}

.c-marked__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
}

/* the name and the chips run on as one wrapping line */
.c-marked__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.c-marked__head :deep(.c-tags) { display: contents; }

.c-marked__notes {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  text-align: start;
}

/* Not found: the amount is struck through, the product steps back. */
.c-card--oos .c-card__title,
.c-card--oos .c-marked__photo { opacity: 0.55; }

/* ── Shape C ── */
.c-compact {
  display: flex;
  align-items: center;
  gap: 12px;
}

.c-compact__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
}
</style>
